"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { deriveLocaleFromPath } from "@/lib/i18n";
import { getMessages } from "@/lib/messages";

/**
 * Top-of-page rebrand announcement: "AXC is now CloudAxe".
 *
 * Sticky + high z-index so SiteAtmosphere's `fixed inset-0` layer cannot
 * paint over it. Inline styles for critical colors so visibility does not
 * depend on Tailwind generating optional utility classes. Auto-hides after
 * 1 minute.
 */
const HIDE_AFTER_MS = 60_000;
const COLLAPSE_MS = 450;

export function AnnouncementBanner() {
  const [collapsed, setCollapsed] = useState(false);
  const [gone, setGone] = useState(false);
  const pathname = usePathname() || "/";
  const { locale } = deriveLocaleFromPath(pathname);
  const m = getMessages(locale);

  useEffect(() => {
    let hideTimer: ReturnType<typeof setTimeout> | undefined;
    let collapseTimer: ReturnType<typeof setTimeout> | undefined;

    try {
      hideTimer = setTimeout(() => {
        setCollapsed(true);
        collapseTimer = setTimeout(() => setGone(true), COLLAPSE_MS);
      }, HIDE_AFTER_MS);
    } catch (error) {
      // Fail open: keep the banner visible rather than crash the page.
      console.error("AnnouncementBanner: failed to schedule auto-hide", error);
    }

    return () => {
      if (hideTimer !== undefined) clearTimeout(hideTimer);
      if (collapseTimer !== undefined) clearTimeout(collapseTimer);
    };
  }, []);

  if (gone) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 9999,
        maxHeight: collapsed ? 0 : 48,
        opacity: collapsed ? 0 : 1,
        overflow: "hidden",
        transition: "max-height 450ms ease-in-out, opacity 450ms ease-in-out",
        pointerEvents: collapsed ? "none" : "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.5rem",
          padding: "0.5rem 1rem",
          textAlign: "center",
          fontSize: "0.875rem",
          fontWeight: 500,
          lineHeight: 1.25,
          color: "#ffffff",
          background: "#27598e",
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.12)",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.375rem" }}>
          <span
            aria-hidden="true"
            style={{
              display: "inline-block",
              width: 6,
              height: 6,
              borderRadius: "9999px",
              background: "rgba(255,255,255,0.9)",
            }}
          />
          {m.banner.axcIsNow}{" "}
          <span
            style={{
              fontWeight: 600,
              letterSpacing: "0.025em",
              textDecoration: "underline",
              textDecorationColor: "rgba(255,255,255,0.7)",
              textDecorationThickness: 2,
              textUnderlineOffset: 2,
            }}
          >
            CloudAxe
          </span>
        </span>
      </div>
    </div>
  );
}
